import { Card, Table, Button } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { useReportPacks } from './hooks/useReportPacks';
import { useDownloadReportPack } from './hooks/useDownloadReportPack';
import { useUser } from '../../../../../../utils/UserContext';
import { ReportPackStatusTag } from './shared/ReportPackStatusTag';
import { REPORT_PACK_STATUS } from '../../../../../../constants/accounting';
import { DemoDataAlert } from '../../home/components/DemoDataAlert';
import { DemoDataWrapper } from '../../home/components/DemoDataBadge';
import { useIsPreActiveCustomer } from '../../../../../../hooks/useIsPreActiveCustomer';

export function ReportPackList() {
  const { customerId: paramCustomerId } = useParams<{ customerId: string }>();
  const { userId } = useUser();
  const customerId = paramCustomerId ?? userId ?? '';
  const { data, isLoading } = useReportPacks(customerId);
  const download = useDownloadReportPack();
  const isPreActive = useIsPreActiveCustomer();

  return (
    <div className="flex h-full w-full max-w-[1148px] flex-col space-y-[16px] overflow-y-auto overflow-x-hidden pb-4 pt-[16px]">
      {isPreActive && <DemoDataAlert />}

      {/* The back link lives in the Card title, not the body, so it survives
          the `loading` skeleton — this screen is otherwise a dead end, and
          ReportPackDetail's back arrow lands here rather than on the hub. */}
      <DemoDataWrapper showBadge={isPreActive}>
        <Card
          title={
            <div className="flex items-center gap-3">
              <Link
                to=".."
                relative="path"
                aria-label="Back to Accounting"
                style={{ fontSize: 18, lineHeight: 1 }}
              >
                ←
              </Link>
              <span>All Accounting Reports</span>
            </div>
          }
          loading={isLoading}
        >
          <Table
            dataSource={data ?? []}
            rowKey="report_pack_id"
            columns={[
              { title: 'Year', dataIndex: 'report_year', width: 90 },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 130,
                render: (s: string) => <ReportPackStatusTag status={s} />,
              },
              {
                title: 'Generated',
                dataIndex: 'created_at',
                width: 200,
                render: (v: string) => new Date(v).toLocaleString(),
              },
              {
                title: '',
                key: 'actions',
                width: 180,
                render: (_: any, p: any) => (
                  <>
                    <Link to={`${p.report_pack_id}`}>View</Link>
                    {p.status === REPORT_PACK_STATUS.Ready && (
                      <>
                        {' · '}
                        {/* No ZIP exists behind a demo pack, and the download
                          endpoint is active-only. The mutation is shared by
                          every row, so match on `variables` — otherwise one
                          click spins every Download button on the page. */}
                        <Button
                          type="link"
                          size="small"
                          disabled={isPreActive}
                          loading={
                            download.isPending &&
                            download.variables === p.report_pack_id
                          }
                          onClick={() => download.mutate(p.report_pack_id)}
                        >
                          Download
                        </Button>
                      </>
                    )}
                  </>
                ),
              },
            ]}
            pagination={{ pageSize: 25 }}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      </DemoDataWrapper>
    </div>
  );
}
